import { RoutesDefinition } from '@overa/base';
import { PartialMatchRouteSnapshot, Routes, UrlMatchResult, UrlSegment } from '@angular/router';
import { Injectable, InjectionToken, inject } from '@angular/core';
import { AuthGuard } from '@overa/security';
import { LayoutResolver } from '@overa/shared';
import { uiModuleRoutes } from '@overa/components/workspace';
import { OveraNotificationUi } from '@overa/notification';
import { PreloadAllModules } from '@angular/router';

import { LayoutComponent } from './components/shared/layout/layout.component';
import { consoleCapabilityGuard, consoleManagesGuard, consoleRootGuard } from './core/console/console.guard';
import { ConsoleCapability } from './core/console/console.model';
import { noPlatformGuard, platformGuard } from './core/platform/platform.guard';
import { PlatformService } from './core/platform/platform.service';

/**
 * El BackOffice se estructura como un lanzador de aplicaciones: primero se elige **plataforma**
 * —Activity, Harley, SecondLawyers— y todo lo que se administra después es de esa plataforma.
 *
 * Dos niveles, y se distinguen por la dirección:
 * - **Primer nivel** (`/launcher`, `/platforms`): por encima de cualquier plataforma. Solo vive aquí
 *   el catálogo de plataformas; no es una zona donde ir metiendo pantallas.
 * - **Plataforma** (`/p/{code}/...`): las pantallas de siempre. La plataforma va en la URL y no en un
 *   servicio para que recargar o compartir un enlace no cambie en silencio sobre qué se opera; ver
 *   `platformGuard`.
 */
@Injectable({ providedIn: 'root' })
export class RoutesService {
  getRoutesDefinition(): RoutesDefinition {
    return {
      routes: [
        {
          path: '',
          component: LayoutComponent,
          pathMatch: 'prefix',
          canActivate: [AuthGuard],
          canActivateChild: [AuthGuard],
          resolve: { layout: LayoutResolver },
          children: [
            { path: '', redirectTo: 'launcher', pathMatch: 'full' },
            {
              path: 'launcher',
              canActivate: [noPlatformGuard],
              loadComponent: () =>
                import('./modules/launcher/platform-launcher.component').then(
                  (m) => m.PlatformLauncherComponent,
                ),
              data: { breadcrumb: null },
            },
            {
              // El cableado era una pantalla aparte y ahora es una pestaña de `/platforms`. La ruta se
              // conserva redirigiendo porque estuvo publicada: era el destino del enlace del catálogo y
              // puede estar guardada en marcadores. Antes que 'platforms' porque consume dos segmentos.
              path: 'platforms/wiring',
              redirectTo: 'platforms',
              pathMatch: 'full',
            },
            {
              // Las plataformas de esta instalación: el resumen de todas y la administración de cada una.
              // Primer nivel porque describe la instalación entera: desde dentro de una plataforma no se
              // podría cablear otra.
              //
              // **Sin guard de capacidad a propósito.** El resumen —qué plataformas hay y en qué estado—
              // lo ve todo el que entra en el BackOffice, que es lo que responde a «¿por qué no veo
              // Harley?». Es la pantalla la que se degrada a solo lectura sin capacidad de consola, en vez
              // de desaparecer del menú: la puerta de verdad la aplica el BFF en cada llamada.
              //
              // One route for `/platforms`, `/platforms/{code}` and `/platforms/{code}/{tab}` (see
              // `platformAdminMatcher`): with three routes Angular would rebuild the component on every
              // tab click and reload the whole catalogue each time.
              matcher: platformAdminMatcher,
              canActivate: [noPlatformGuard],
              loadComponent: () =>
                import('./modules/platforms/platform-admin.component').then(
                  (m) => m.PlatformAdminComponent,
                ),
              data: { breadcrumb: null },
            },
            {
              // The IdP this BackOffice is bound to, read only (docs/PLATAFORMAS-APLICACIONES-IDPS.md §3.1).
              path: 'console/idp',
              canActivate: [noPlatformGuard, consoleManagesGuard()],
              loadComponent: () =>
                import('./modules/console/console-identity-provider.component').then(
                  (m) => m.ConsoleIdentityProviderComponent,
                ),
              data: { breadcrumb: null },
            },
            {
              // The catalog of common services: URL, scope, introspection client and secret of each shared
              // service (docs/PLATAFORMAS-APLICACIONES-IDPS.md §11). Read only without platforms.create.
              path: 'console/common-services',
              canActivate: [noPlatformGuard, consoleManagesGuard()],
              loadComponent: () =>
                import('./modules/console/console-common-services.component').then(
                  (m) => m.ConsoleCommonServicesComponent,
                ),
              data: { breadcrumb: null },
            },
            {
              // Infrastructure integrations (Azure DevOps, ArgoCD) and deployment targets of new applications
              // (docs/INFRAESTRUCTURA-BACKOFFICE.md §10.9). Read only without infra.integrations.
              path: 'console/integrations',
              canActivate: [noPlatformGuard, consoleCapabilityGuard(ConsoleCapability.InfraView)],
              loadComponent: () =>
                import('./modules/console/console-integrations.component').then(
                  (m) => m.ConsoleIntegrationsComponent,
                ),
              data: { breadcrumb: null },
            },
            {
              // Groups of platforms and the common services each one can use (§3.0, §11).
              path: 'console/groups',
              canActivate: [noPlatformGuard, consoleManagesGuard()],
              loadComponent: () =>
                import('./modules/console/console-groups.component').then(
                  (m) => m.ConsoleGroupsComponent,
                ),
              data: { breadcrumb: null },
            },
            {
              // Quién puede gestionar el catálogo de plataformas. Es del primer nivel porque la
              // capacidad que reparte está por encima de todos los tenants y de todas las plataformas;
              // colgarla de una plataforma diría que se acota a ella, y no se acota.
              //
              // El guard es la SEGUNDA puerta: la de verdad la aplica Identity en cada llamada. Aquí
              // solo se evita enseñar una pantalla que no podría hacer nada.
              path: 'console/operators',
              canActivate: [noPlatformGuard, consoleCapabilityGuard(ConsoleCapability.ManageOperators)],
              loadComponent: () =>
                import('./modules/console/console-operators.component').then(
                  (m) => m.ConsoleOperatorsComponent,
                ),
              data: { breadcrumb: null },
            },
            {
              // El servicio de IA es uno solo para todas las plataformas, así que su **definición
              // global** —configuración, catálogo de agentes, skills, tiers, herramientas, enrutado y
              // activos— se administra aquí arriba. Lo que cada inquilino cambia encima se administra
              // dentro de su plataforma, en `ai-admin`. Son las mismas pantallas: el modo lo deduce
              // `AiScopeService` de si hay plataforma abierta.
              //
              // El destino por defecto es la configuración y no `usage`, que es el del módulo: el
              // consumo se mira por inquilino y en el primer nivel no hay ninguno.
              path: 'ai',
              pathMatch: 'full',
              redirectTo: 'ai/config',
            },
            {
              path: 'ai',
              canActivate: [noPlatformGuard, consoleRootGuard()],
              loadChildren: () =>
                import('./modules/admin/ai-admin/ai-admin.module').then((m) => m.AiAdminModule),
              data: { breadcrumb: null },
            },
            {
              path: `p/:platform`,
              canActivate: [platformGuard],
              loadChildren: () =>
                import('./modules/admin/admin.module').then((m) => m.AdminModule),
              data: { breadcrumb: null },
            },
            {
              // Compatibilidad con las direcciones anteriores, que no llevaban plataforma. Cubre los
              // enlaces ya repartidos y también `dynamicListParentRoute`, que la librería dinámica lee
              // de la configuración y no puede depender de la plataforma activa.
              path: 'admin/**',
              redirectTo: redirectToActivePlatform,
            },
            // core/notifications (la campana): pantalla de @overa/notification. Antes que 'core', que dejará de traerla.
            ...uiModuleRoutes([OveraNotificationUi]),
            {
              path: 'core',
              loadChildren: () =>
                import('@overa/base').then((m) => m.LibRoutingModule),
              data: { breadcrumb: null },
            },
          ],
        },
      ],
    };
  }
}

/**
 * Matches `/platforms`, `/platforms/{code}` and `/platforms/{code}/{tab}` as a single route.
 *
 * The open platform and tab live in the URL so they can be linked and survive a reload. `platforms/wiring`
 * is declared before this route and wins, which is why `wiring` is a reserved platform code in the BFF.
 */
export function platformAdminMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  if (segments[0]?.path !== 'platforms' || segments.length > 3) return null;

  const posParams: Record<string, UrlSegment> = {};
  if (segments[1]) posParams['code'] = segments[1];
  if (segments[2]) posParams['tab'] = segments[2];

  return { consumed: segments, posParams };
}

/**
 * Traslada una dirección `/admin/...` a la plataforma en la que se estaba.
 *
 * Si no hay ninguna resoluble —primera visita, o el catálogo todavía no ha llegado— se va al lanzador
 * en vez de elegir una por el usuario: abrir una plataforma que nadie ha pedido es exactamente lo que
 * este rediseño viene a evitar.
 */
function redirectToActivePlatform(redirectData: PartialMatchRouteSnapshot): string {
  const platforms = inject(PlatformService);
  const code = platforms.active()?.code ?? platforms.resolveDefaultCode();
  if (!code) return '/launcher';

  const rest = redirectData.url
    .map((segment) => segment.path)
    .slice(1) // fuera el propio 'admin'
    .join('/');

  return rest ? `/p/${code}/${rest}` : `/p/${code}`;
}

export class RoutesSettings {
  private static routesService: RoutesService;

  public static initialize(routesService: RoutesService) {
    RoutesSettings.routesService = routesService;
  }

  public static get ROUTES(): Routes {
    if (!RoutesSettings.routesService) {
      return [];
    }
    return RoutesSettings.routesService.getRoutesDefinition().routes || [];
  }
}

export const APP_ROUTES = new InjectionToken<Routes>('APP_ROUTES', {
  providedIn: 'root',
  factory: () => inject(RoutesService).getRoutesDefinition()?.routes || [],
});
